/**
 * Labstep
 *
 * @module components/Molecule/DataGrid/coldefs/safety
 * @desc Column definition for Safety fields
 */

import type { ValueGetterParams } from 'ag-grid-community';
import MoleculeSafety from 'labstep-web/components/Molecule/Safety';
import { MoleculeSafetyPictograms as Pictograms } from 'labstep-web/components/Molecule/Safety/Pictograms';
import { EntityReadEntityContainer } from 'labstep-web/containers/Entity/Read/Entity';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type {
  CellEditorParams,
  CellRendererParams,
} from 'labstep-web/core/DataGrid/types';
import {
  getEditable,
  getEntityDefault,
  stopEditingAndFocus,
} from 'labstep-web/core/DataGrid/utils';
import type { Entity } from 'labstep-web/models/entity.model';
import { Molecule } from 'labstep-web/models/molecule.model';
import type { colDefMoleculeType } from '../types';

/**
 * Column definition for Safety fields
 * @param field Field (IUPACName, MolecularWeight, Density, MolecularFormula, CAS)
 * @param options Options
 * @returns Column definition
 */
const colDefSafety: colDefMoleculeType = (options = {}) => {
  const { getNestedEntity, columnProps, getCreateProps } = options;
  const getEntity = getNestedEntity || getEntityDefault;
  const editable = getEditable(getNestedEntity);

  return {
    colId: 'pubchem_safety',
    cellEditor: (params: CellEditorParams<Entity>) => {
      const entity = getEntity(params as any);
      if (!(entity instanceof Molecule) || !entity.properties) {
        return null;
      }
      return (
        <EntityReadEntityContainer
          entityName={Molecule.entityName}
          id={entity.idAttr}
        >
          {({ entity: molecule }) => {
            if (!molecule || !molecule.properties) {
              return null;
            }
            return (
              <MoleculeSafety
                parent={molecule}
                pubChemMetadata={molecule.properties}
                modalProps={{
                  open: true,
                  onClose: () => stopEditingAndFocus(params),
                  viewComponent: null,
                }}
              />
            );
          }}
        </EntityReadEntityContainer>
      );
    },
    cellEditorPopup: true,
    cellRenderer: (params: CellRendererParams<Entity>) => {
      if (!params.data) {
        return '';
      }
      const entity = getEntity(params);
      if (
        !(entity instanceof Molecule) ||
        !entity.properties ||
        entity.properties.Safety.pictograms?.length === 0
      ) {
        return (
          <DataGridPlaceholder
            params={params}
            children="Select"
            editable={editable}
          />
        );
      }
      return (
        <Pictograms
          pubChemMetadata={entity.properties}
          isInline
          size="mini"
        />
      );
    },
    headerName: 'Safety',
    editable: (params: any) =>
      editable(params) &&
      !!(getEntity(params) as any)?.properties &&
      // templating not supported yet
      !getCreateProps?.(params),
    valueGetter: (params: ValueGetterParams): string | null => {
      const entity = getEntity(params);
      if (entity instanceof Molecule && entity.properties) {
        return JSON.stringify(entity.properties.Safety);
      }
      return null;
    },
    ...columnProps,
  };
};

export default colDefSafety;
